/**
 * Document Management Hook
 * 
 * Provides comprehensive document management for various entity types (jobs, RFQs,
 * bids, completions, specifications). Handles file upload to Supabase Storage,
 * metadata tracking, signed URL generation, and document deletion.
 * 
 * **Supported Entity Types:**
 * - job: Job-related documents (specs, drawings, requirements)
 * - rfq: RFQ attachments and addenda
 * - bid: Vendor bid attachments and proposals
 * - completion: Job completion evidence and documentation
 * - specification: Specification sheets and technical docs
 * 
 * **File Requirements:**
 * - Maximum size: 200MB per file
 * - Allowed types: PDF, JPG, PNG, DOCX, CSV, XLSX
 * - Storage location: Supabase Storage 'documents' bucket
 * - Path structure: {userId}/{entityType}/{entityId}/{randomFilename}
 * 
 * **Upload Process:**
 * 1. Client-side file validation (size, type)
 * 2. Generate unique filename to prevent collisions
 * 3. Upload file to Supabase Storage
 * 4. Create document metadata record in database
 * 5. Return document record with storage reference
 * 
 * **Security:**
 * - Organization-scoped access via RLS policies
 * - Signed URLs for temporary access (1 hour)
 * - User authentication required
 * - Storage key protection
 * 
 * **Key Features:**
 * - Multi-entity document management
 * - File type and size validation
 * - Secure file upload to storage
 * - Signed URL generation for viewing
 * - Document deletion (storage + database)
 * - Upload progress tracking
 * - Automatic refetch after mutations
 * 
 * **Use Cases:**
 * - Job requirement documents
 * - RFQ specifications and drawings
 * - Vendor proposal attachments
 * - Completion proof documentation
 * - Technical specification sheets
 * 
 * @param options - Entity type and ID for document scoping
 * @returns {Object} Document management functions and state
 * @returns {Document[]} documents - Array of documents for entity
 * @returns {boolean} loading - Loading state
 * @returns {string|null} error - Error message if any
 * @returns {boolean} uploading - Upload in progress
 * @returns {Function} uploadDocument - Upload new document
 * @returns {Function} getDownloadUrl - Get signed URL for viewing
 * @returns {Function} deleteDocument - Delete document completely
 * @returns {Function} refetch - Manually refresh documents
 * @returns {Function} validateFile - Validate file before upload
 * 
 * @example
 * // Upload job specification document
 * const { uploadDocument, uploading } = useDocuments({
 *   entityType: 'job',
 *   entityId: jobId
 * });
 * 
 * const handleUpload = async (file: File) => {
 *   try {
 *     const document = await uploadDocument(
 *       file,
 *       'Technical Specifications',
 *       'Detailed specs for custom machined parts'
 *     );
 *     toast({ title: 'Document uploaded', description: document.filename });
 *   } catch (error) {
 *     toast({ 
 *       title: 'Upload failed', 
 *       description: error.message,
 *       variant: 'destructive' 
 *     });
 *   }
 * };
 * 
 * @example
 * // Download/view document
 * const { documents, getDownloadUrl } = useDocuments({
 *   entityType: 'completion',
 *   entityId: completionId
 * });
 * 
 * const handleViewDocument = async (document: Document) => {
 *   const url = await getDownloadUrl(document);
 *   window.open(url, '_blank');
 * };
 * 
 * @example
 * // Delete document
 * const { deleteDocument } = useDocuments({
 *   entityType: 'bid',
 *   entityId: bidId
 * });
 * 
 * const handleDelete = async (documentId: string) => {
 *   const confirmed = await showConfirmDialog({
 *     title: 'Delete Document',
 *     message: 'This will permanently delete the document.'
 *   });
 *   
 *   if (confirmed) {
 *     await deleteDocument(documentId);
 *     toast({ title: 'Document deleted' });
 *   }
 * };
 * 
 * @example
 * // Validate before upload
 * const { validateFile, uploadDocument } = useDocuments({
 *   entityType: 'rfq',
 *   entityId: rfqId
 * });
 * 
 * const handleFileSelect = async (file: File) => {
 *   const error = validateFile(file);
 *   if (error) {
 *     toast({ title: 'Invalid file', description: error });
 *     return;
 *   }
 *   
 *   await uploadDocument(file);
 * };
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Document, MAX_FILE_SIZE, ALLOWED_FILE_TYPES } from '@/lib/types/completion';
import { 
  TUS_THRESHOLD,
  SUPABASE_URL,
  createTusUpload, 
  createProgressUpload,
  UploadAbortedError,
  type UploadProgress,
  type UploadController 
} from '@/lib/upload';

interface UseDocumentsOptions {
  entityType: 'job' | 'rfq' | 'bid' | 'completion' | 'specification';
  entityId: string;
}

export const useDocuments = ({ entityType, entityId }: UseDocumentsOptions) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const uploadControllerRef = useRef<UploadController | null>(null);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setDocuments((data as Document[]) || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (entityId) {
      fetchDocuments();
    }
  }, [entityType, entityId]);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 200MB limit`;
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return `File type not allowed. Allowed types: PDF, JPG, PNG, DOCX, CSV, XLSX`;
    }

    return null;
  };

  /**
   * Ensures a fresh access token for long uploads
   * Refreshes the session to prevent token expiry during large file uploads
   */
  const ensureFreshToken = async (): Promise<string> => {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session) {
      throw new Error('Failed to refresh authentication session');
    }
    return data.session.access_token;
  };

  const uploadDocument = async (file: File, title?: string, description?: string) => {
    try {
      setUploading(true);
      setUploadProgress({ bytesUploaded: 0, bytesTotal: file.size, percentage: 0 });
      
      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        throw new Error(validationError);
      }

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Refresh token to ensure it's valid for the entire upload duration
      const accessToken = await ensureFreshToken();

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', userData.user.id)
        .single();

      if (!profile) throw new Error('User profile not found');

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const storageKey = `${userData.user.id}/${entityType}/${entityId}/${fileName}`;

      // Decide upload method based on file size
      const useTus = file.size >= TUS_THRESHOLD;

      // Upload to Supabase Storage with progress tracking
      await new Promise<void>((resolve, reject) => {
        const handleProgress = (progress: UploadProgress) => {
          setUploadProgress(progress);
        };

        const handleError = (err: Error) => {
          uploadControllerRef.current = null;
          reject(err);
        };

        const handleSuccess = () => {
          uploadControllerRef.current = null;
          setUploadProgress({ 
            bytesUploaded: file.size, 
            bytesTotal: file.size, 
            percentage: 100 
          });
          resolve();
        };

        const handleAbort = () => {
          uploadControllerRef.current = null;
          reject(new UploadAbortedError());
        };

        if (useTus) {
          // Use TUS for large files (resumable)
          const controller = createTusUpload({
            file: file,
            bucketName: 'documents',
            objectPath: storageKey,
            accessToken,
            onProgress: handleProgress,
            onError: handleError,
            onSuccess: handleSuccess,
            onAbort: handleAbort,
          });
          uploadControllerRef.current = controller;
          controller.start();
        } else {
          // Use XHR for smaller files (simpler, with progress)
          const url = `${SUPABASE_URL}/storage/v1/object/documents/${storageKey}`;
          const controller = createProgressUpload({
            file: file,
            url,
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'x-upsert': 'true',
            },
            onProgress: handleProgress,
            onError: handleError,
            onSuccess: handleSuccess,
            onAbort: handleAbort,
          });
          uploadControllerRef.current = controller;
          controller.start();
        }
      });

      // Create document record
      const { data: documentData, error: documentError } = await supabase
        .from('documents')
        .insert({
          organization_id: profile.organization_id,
          entity_type: entityType,
          entity_id: entityId,
          filename: fileName,
          original_filename: file.name,
          size_bytes: file.size,
          content_type: file.type,
          storage_key: storageKey,
          uploaded_by_user_id: userData.user.id,
        })
        .select()
        .single();

      if (documentError) throw documentError;

      await fetchDocuments();
      return documentData;
    } catch (err) {
      // Check if it was an intentional cancellation - clean exit without error
      if (err instanceof UploadAbortedError) {
        return null;
      }
      console.error('Error uploading document:', err);
      console.error('Error details:', JSON.stringify(err, null, 2));
      if (err instanceof Error) {
        console.error('Error message:', err.message);
        console.error('Error stack:', err.stack);
      }
      throw err;
    } finally {
      setUploading(false);
      setUploadProgress(null);
      uploadControllerRef.current = null;
    }
  };

  /**
   * Cancels the current upload in progress
   */
  const cancelUpload = () => {
    if (uploadControllerRef.current) {
      uploadControllerRef.current.abort();
      uploadControllerRef.current = null;
      setUploading(false);
      setUploadProgress(null);
    }
  };

  /**
   * Pauses the current TUS upload (only works for large files using TUS)
   */
  const pauseUpload = () => {
    if (uploadControllerRef.current?.pause) {
      uploadControllerRef.current.pause();
    }
  };

  /**
   * Resumes a paused TUS upload
   */
  const resumeUpload = () => {
    if (uploadControllerRef.current?.resume) {
      uploadControllerRef.current.resume();
    }
  };

  const getDownloadUrl = async (document: Document) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(document.storage_key, 3600); // 1 hour expiration

      if (error) throw error;
      return data.signedUrl;
    } catch (err) {
      console.error('Error getting download URL:', err);
      throw err;
    }
  };

  const deleteDocument = async (documentId: string) => {
    try {
      const document = documents.find(d => d.id === documentId);
      if (!document) throw new Error('Document not found');

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([document.storage_key]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .eq('id', documentId);

      if (dbError) throw dbError;

      await fetchDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      throw err;
    }
  };

  return {
    documents,
    loading,
    error,
    uploading,
    uploadProgress,
    uploadDocument,
    cancelUpload,
    pauseUpload,
    resumeUpload,
    getDownloadUrl,
    deleteDocument,
    refetch: fetchDocuments,
    validateFile
  };
};
